import React, { useEffect } from "react";
import { motion } from "framer-motion";
import ReactGA from 'react-ga4';
import { useLocation } from "react-router-dom";
import { ChevronDown } from 'lucide-react';
import TeamCard from './TeamCard';

export default function AboutUs() {
    const location = useLocation();

    // GA: page view for About Us
    useEffect(() => {
        try {
            ReactGA.send({ hitType: 'pageview', page: location.pathname || '/about', title: 'About Us' });
        } catch { }
    }, [location.pathname]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const partners = [
        { img: "assets/nvidia-inceptio-program-logo.jpg" },
        { img: "assets/ms-startups-founders-hub-logo.png" },
        { img: "assets/google-cloud-logo.png" },
        { img: "assets/fsid-iisc-logo-2.png" },
        { img: "assets/psb_logo.png" },
        { img: "assets/govtka_logo.png" },
    ];

    return (
        <div className="bg-white">
            {/* Hero Section - Mission Statement */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-charcoal">
                {/* Animated Background */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-neon-lime/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                {/* Content */}
                <div className="relative z-10 container mx-auto px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-6">Our Commitment</p>
                        <h1 className="text-5xl md:text-7xl font-black text-white uppercase tracking-tight leading-[1.1] mb-8">
                            Empowering <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">10 Million Athletes</span> <br /> in 10 Years
                        </h1>
                        <p className="text-light-gray text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
                            Building the future of sports intelligence, one athlete at a time
                        </p>
                    </motion.div>
                </div>

                {/* Scroll Down Indicator - Bottom Center */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.8 }}
                    className="absolute bottom-8 left-1/2 -translate-x-1/2 cursor-pointer group flex flex-col items-center gap-2 z-20"
                    onClick={() => {
                        const nextSection = document.querySelector('section.py-12');
                        if (nextSection) {
                            nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }}
                >
                    <span className="text-gray-400 text-sm uppercase tracking-[0.3em] font-light group-hover:text-emerald transition-colors duration-300">
                        SCROLL
                    </span>
                    <motion.div
                        animate={{
                            y: [0, 8, 0],
                        }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                    >
                        <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-emerald transition-colors duration-300" strokeWidth={1.5} />
                    </motion.div>
                </motion.div>
            </section>

            {/* Platform Overview Section */}
            <section className="relative py-12 bg-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-5xl mx-auto"
                    >
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase text-center mb-12">
                            Built for the <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Next Million Athletes</span>
                        </h2>

                        <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-3xl p-8 md:p-12 shadow-xl">
                            <p className="text-gray-700 text-lg md:text-xl lg:text-2xl leading-relaxed text-center">
                                <span className="font-bold text-gray-900">VISIST.AI</span> is a visual intelligence platform reshaping how athletes and coaches train, starting from badminton and now expanding into cricket and other racket sports. By combining <span className="text-emerald font-semibold">computer vision</span>, <span className="text-emerald font-semibold">sports science</span>, and <span className="text-emerald font-semibold">agentic AI</span>, VISIST.AI delivers deep skill and movement analysis from everyday training and match videos—<span className="font-semibold text-gray-900">no expensive hardware or complex setups</span>.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Why Now Section */}
            <section className="relative py-12 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">The Perfect Moment</p>
                        <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase mb-8">Why Now?</h2>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                        {/* Multi-Sport Expansion */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="group relative bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-emerald/10 to-emerald/5 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                                <svg className="w-8 h-8 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-emerald transition-colors duration-300">Multi-Sport Expansion</h3>
                            <p className="text-gray-600 text-base leading-relaxed">
                                The sports tech market is rapidly evolving, with athletes and academies seeking scalable, data-driven solutions across multiple disciplines. Our platform's expansion from badminton to cricket and racket sports positions us at the forefront of this transformation.
                            </p>
                        </motion.div>

                        {/* AI/Vision Advances */}
                        <motion.div
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="group relative bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                        >
                            <div className="w-16 h-16 bg-gradient-to-br from-neon-lime/10 to-emerald/5 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                                <svg className="w-8 h-8 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-black text-gray-900 mb-4 group-hover:text-emerald transition-colors duration-300">AI & Vision Breakthroughs</h3>
                            <p className="text-gray-600 text-base leading-relaxed">
                                Recent advances in computer vision and agentic AI have made sophisticated movement analysis accessible and affordable. We're leveraging these breakthroughs to democratize elite-level coaching insights for athletes at every level.
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Vision & Mission Section */}
            <section className="relative py-12 bg-deep-charcoal overflow-hidden">
                {/* Decorative Background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-20 right-10 w-96 h-96 bg-neon-lime/10 rounded-full blur-3xl"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">Our North Star</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase">
                            Vision & <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Mission</span>
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-8 max-w-4xl mx-auto">
                        {/* Vision Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="group relative"
                        >
                            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border-2 border-white/20 rounded-3xl p-8 md:p-10 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
                                {/* Gradient Accent */}
                                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald to-neon-lime rounded-t-3xl"></div>

                                {/* Icon */}
                                <div className="w-16 h-16 bg-gradient-to-br from-emerald/20 to-emerald/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                                    <svg className="w-8 h-8 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>

                                <h3 className="text-3xl font-black text-white mb-6 group-hover:text-emerald transition-colors duration-300">Vision</h3>
                                <p className="text-light-gray text-lg leading-relaxed">
                                    To make elite sports intelligence accessible to every athlete, any where — and enabling a billion training moments to translate into higher performance, faster career  growth and healthier outcomes.
                                </p>
                            </div>
                        </motion.div>

                        {/* Mission Card */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="group relative"
                        >
                            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border-2 border-white/20 rounded-3xl p-8 md:p-10 hover:border-neon-lime transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 overflow-hidden">
                                {/* Gradient Accent */}
                                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-neon-lime to-emerald rounded-t-3xl"></div>

                                {/* Icon */}
                                <div className="w-16 h-16 bg-gradient-to-br from-neon-lime/20 to-emerald/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
                                    <svg className="w-8 h-8 text-neon-lime" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                </div>

                                <h3 className="text-3xl font-black text-white mb-6 group-hover:text-neon-lime transition-colors duration-300">Mission</h3>
                                <p className="text-light-gray text-lg leading-relaxed">
                                    To democratize elite coaching by building an AI-powered visual intelligence platform that turns everyday sports videos into actionable insights — empowering athletes, coaches, and academies to raise performance, prevent injuries, and unlock champions at scale.
                                </p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Our Team Section - Tactical Data Cards */}
            <section className="relative py-8 bg-midnight-blue overflow-hidden">
                <div className="container mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-20"
                    >
                        <p className="text-electric-lime text-sm md:text-base font-bold uppercase tracking-widest mb-3 font-mono">PERSONNEL MANIFEST</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight">
                            The <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-lime to-neon-lime">Squad</span>
                        </h2>
                    </motion.div>

                    {/* SECTION 1: THE TEAM */}
                    <div className="mb-24">
                        <div className="flex items-center gap-4 mb-12">
                            <div className="h-px bg-electric-lime/30 flex-grow"></div>
                            <h3 className="text-xl text-electric-lime font-mono tracking-widest uppercase">THE TEAM</h3>
                            <div className="h-px bg-electric-lime/30 flex-grow"></div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-6 max-w-7xl mx-auto">
                            <TeamCard
                                name="Mr. Suresh Chintada"
                                role="Founder & MD"
                                id="EXE-001"
                                image="assets/Suresh.jpeg"
                                linkedInLink="https://www.linkedin.com/in/schintada/"
                                imagePosition="object-top object-cover"
                            />
                            <TeamCard
                                name="Mr. Seshadri Raghavan"
                                role="Co-Founder & CTO"
                                id="EXE-002"
                                image="assets/Sesha.jpeg"
                                linkedInLink="https://www.linkedin.com/in/seshar/"
                                imagePosition="object-top object-cover"
                            />
                            <TeamCard
                                name="Mr. Arun Karunanithi"
                                role="Software Engineer"
                                id="ENG-04"
                                image="/assets/Arun.jpeg"
                                linkedInLink="https://www.linkedin.com/in/arunkarnan/"
                            />
                            <TeamCard
                                name="Mr. Benoush Valentine F"
                                role="Software Engineer"
                                id="DEV-03"
                                image="assets/Benoush.jpg"
                                linkedInLink="https://www.linkedin.com/in/benoush-valentine-f-965a27252/"
                            />
                            <TeamCard
                                name="Dr. Aniruddh Sikdar"
                                role="Consulting Engineer"
                                id="EXE-002"
                                image="assets/Anirudh.jpg"
                                linkedInLink="https://www.linkedin.com/in/aniruddh-sikdar-723112b8/"
                                imagePosition="object-top object-cover"
                            />
                            <TeamCard
                                name="Mr. Rohan Lall"
                                role="Software Engineer"
                                id="QA-05"
                                image="assets/Rohan.png"
                                linkedInLink="https://www.linkedin.com/in/rohan-lall-7938b326b/"
                            />
                            <TeamCard
                                name="Mr. Shashank Jadhav"
                                role="AI & ML Engineer"
                                id="AI-07"
                                image="assets/Shashank.jpeg"
                                linkedInLink="https://www.linkedin.com/in/shashank-jadhav-0407a2240/"
                            />


                        </div>
                    </div>

                    {/* SECTION 2: THE COUNCIL */}
                    <div className="mb-24">
                        <div className="flex items-center gap-4 mb-12">
                            <div className="h-px bg-gold-border/30 flex-grow"></div>
                            <h3 className="text-xl text-gold-border font-mono tracking-widest uppercase">THE ADVISORS</h3>
                            <div className="h-px bg-gold-border/30 flex-grow"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto justify-items-center">
                            <TeamCard
                                name="Prof Suresh Sundaram"
                                role="Mentor & Advisor - Technical Direction"
                                id="ADV-01"
                                image="assets/Sureshs.jpg"
                                isAdvisor={true}
                                linkedInLink="#"
                                imagePosition="object-top object-cover"
                            />
                            <TeamCard
                                name="Mr. Shrikant Naidu"
                                role="Scalability & Enterprise Arch and Strategy Advisory"
                                id="ADV-02"
                                image="assets/Shrikant.jpg"
                                isAdvisor={true}
                                linkedInLink="https://www.linkedin.com/in/shrikantnaidu/"
                                imagePosition="object-top object-cover"
                            />
                            {/* <TeamCard
                                name="Mr. Vidit Jain"
                                role="Technical Advisory - Computer Vision & ML"
                                id="ADV-03"
                                image="assets/Vidit.jpeg"
                                isAdvisor={true}
                                linkedInLink="https://www.linkedin.com/in/viditjain/"
                                imagePosition="object-top object-cover"
                            /> */}
                        </div>
                    </div>
                </div>
            </section>

            {/* Founding Story Section */}
            < section className="relative py-12 bg-white overflow-hidden" >
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="max-w-4xl mx-auto"
                    >
                        <div className="text-center mb-12">
                            <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">Our Story</p>
                            <h2 className="text-4xl md:text-5xl font-black text-gray-900 uppercase">
                                Founded in <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Bengaluru</span>
                            </h2>
                        </div>

                        <div className="relative bg-gradient-to-br from-gray-50 to-white border-2 border-emerald/30 rounded-3xl p-8 md:p-12 shadow-xl overflow-hidden">
                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald via-neon-lime to-emerald rounded-t-3xl"></div>

                            <p className="text-gray-700 text-xl md:text-2xl leading-relaxed text-center mb-8">
                                Founded in <span className="font-bold text-gray-900">Bengaluru in 2024</span>, VISIST.AI is incubated at <span className="font-bold text-emerald">IISc's FSID program</span> and already powers AI-assisted assessments in dozens of leading academies.
                            </p>

                            {/* Credibility Badges */}
                            <div className="flex flex-wrap items-center justify-center gap-6 pt-6 border-t border-gray-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-emerald/10 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Incubated at</p>
                                        <p className="font-bold text-gray-900">IISc FSID</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-emerald/10 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Based in</p>
                                        <p className="font-bold text-gray-900">Bengaluru, India</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-emerald/10 rounded-full flex items-center justify-center">
                                        <svg className="w-6 h-6 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">Founded</p>
                                        <p className="font-bold text-gray-900">2024</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section >

            {/* Social Proof Section */}
            < section className="relative py-12 bg-gradient-to-b from-gray-50 to-white overflow-hidden" >
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">Trusted Worldwide</p>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-6">
                            Powering <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">70+ Centres</span>
                        </h2>
                        <p className="text-gray-600 text-xl max-w-3xl mx-auto">
                            Leading sports academies trust VISIST.AI to transform their training programs
                        </p>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="text-center p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald transition-all duration-300 hover:shadow-xl"
                        >
                            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime mb-3">70+</div>
                            <p className="text-gray-900 font-bold text-lg mb-2">Centres</p>
                            <p className="text-gray-600 text-sm">Using VISIST.AI</p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="text-center p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald transition-all duration-300 hover:shadow-xl"
                        >
                            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime mb-3">1000+</div>
                            <p className="text-gray-900 font-bold text-lg mb-2">Athletes</p>
                            <p className="text-gray-600 text-sm">Improving Daily</p>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="text-center p-8 bg-white border-2 border-gray-200 rounded-2xl hover:border-emerald transition-all duration-300 hover:shadow-xl"
                        >
                            <div className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime mb-3">10K+</div>
                            <p className="text-gray-900 font-bold text-lg mb-2">Videos</p>
                            <p className="text-gray-600 text-sm">Analyzed</p>
                        </motion.div>
                    </div>
                </div>
            </section >

            {/* Partners Section from Home */}
            < section className="relative py-8 bg-white overflow-hidden" >
                <div className="container mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-12"
                    >
                        <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Trusted By</p>
                        <h2 className="text-2xl md:text-3xl font-black text-gray-900 uppercase">Our Partners</h2>
                    </motion.div>

                    {/* Infinite Scrolling Carousel */}
                    <div className="relative mb-12">
                        <style>{`
                  @keyframes scroll-left {
                    0% {
                      transform: translateX(0);
                    }
                    100% {
                      transform: translateX(-50%);
                    }
                  }
                  .scroll-container {
                    animation: scroll-left 12s linear infinite;
                  }
                  @media (min-width: 768px) {
                    .scroll-container {
                      animation: scroll-left 30s linear infinite;
                    }
                  }
                  .scroll-container:hover {
                    animation-play-state: paused;
                  }
                `}</style>

                        <div className="overflow-hidden">
                            <div className="flex scroll-container">
                                {/* Render partners twice for seamless loop */}
                                {[...partners, ...partners].map((ele, index) => (
                                    <div
                                        key={index}
                                        className="flex-shrink-0 flex items-center justify-center p-1 md:p-6 mx-1 md:mx-8 bg-gray-50 border border-gray-200 rounded-xl hover:border-gray-300 transition-all w-20 h-12 md:w-[240px] md:h-[160px]"
                                    >
                                        <img
                                            src={ele.img}
                                            alt={`Partner ${(index % partners.length) + 1}`}
                                            className="max-w-full max-h-full object-contain opacity-90 hover:opacity-100 transition-all duration-300 scale-100 hover:scale-105"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section >
        </div >
    );
}
